/* eslint-disable react/prop-types */
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { openSnackbar } from 'store/slices/snackbar';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Button,
    Chip,
    ClickAwayListener,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormHelperText,
    IconButton,
    InputAdornment,
    InputLabel,
    LinearProgress,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    OutlinedInput,
    Paper,
    Popper,
    Stack,
    Tooltip,
    Typography
} from '@mui/material';

// third-party
import { FormattedMessage } from 'react-intl';
import PerfectScrollbar from 'react-perfect-scrollbar';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import useAuth from 'hooks/useAuth';

// assets
import { IconKey, IconLock, IconLogout, IconUserCircle } from '@tabler/icons';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import useConfig from 'hooks/useConfig';

const API_BASE = `${process.env.REACT_APP_API_BASE}/api`;

const getGreeting = () => {
    const h = new Date().getHours();
    if (h >= 5 && h < 12) return 'Good Morning';
    if (h >= 12 && h < 17) return 'Good Afternoon';
    if (h >= 17 && h < 21) return 'Good Evening';
    return 'Good Night';
};

const getStrength = (pwd) => {
    if (!pwd) return 0;
    let score = 0;
    if (pwd.length >= 6) score += 1;
    if (pwd.length >= 10) score += 1;
    if (/[A-Z]/.test(pwd)) score += 1;
    if (/[0-9]/.test(pwd)) score += 1;
    if (/[^A-Za-z0-9]/.test(pwd)) score += 1;
    return score;
};

const strengthLabel = (score) => {
    if (score <= 1) return { label: 'Very Weak', color: '#ef4444', value: 20 };
    if (score === 2) return { label: 'Weak', color: '#f97316', value: 40 };
    if (score === 3) return { label: 'Fair', color: '#eab308', value: 60 };
    if (score === 4) return { label: 'Strong', color: '#22c55e', value: 80 };
    return { label: 'Very Strong', color: '#16a34a', value: 100 };
};

// ── Change Password Dialog ─────────────────────────────────────────────────────
function ChangePasswordDialog({ open, onClose, token }) {
    const theme = useTheme();
    const [oldPwd, setOldPwd] = useState('');
    const [newPwd, setNewPwd] = useState('');
    const [confirmPwd, setConfirmPwd] = useState('');
    const [showOld, setShowOld] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    const strength = getStrength(newPwd);
    const { label: strengthText, color: strengthColor, value: strengthValue } = strengthLabel(strength);

    const handleClose = () => {
        setOldPwd('');
        setNewPwd('');
        setConfirmPwd('');
        setError('');
        setSuccess('');
        setLoading(false);
        onClose();
    };

    const handleSubmit = async () => {
        setError('');
        setSuccess('');
        if (!oldPwd || !newPwd || !confirmPwd) {
            setError('All fields are required.');
            return;
        }
        if (newPwd.length < 6) {
            setError('New password must be at least 6 characters.');
            return;
        }
        if (newPwd !== confirmPwd) {
            setError('New passwords do not match.');
            return;
        }

        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/auth/change-password/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ oldPassword: oldPwd, newPassword: newPwd, confirmPassword: confirmPwd })
            });
            const data = await res.json();
            if (!res.ok) {
                setError(data.error || 'Failed to change password.');
            } else {
                setSuccess('Password changed successfully!');
                setTimeout(handleClose, 1500);
            }
        } catch {
            setError('Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const fieldSx = { mb: 2, mt: 1, '& .MuiOutlinedInput-root': { borderRadius: '8px' } };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontWeight: 700, pb: 0 }}>Change Password</DialogTitle>
            <DialogContent sx={{ pt: 3, pb: 3, overflow: 'visible' }}>
                {/* Old Password */}
                <FormControl size="small" fullWidth sx={fieldSx}>
                    <InputLabel>Current Password</InputLabel>
                    <OutlinedInput
                        type={showOld ? 'text' : 'password'}
                        value={oldPwd}
                        label="Current Password"
                        onChange={(e) => setOldPwd(e.target.value)}
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setShowOld(!showOld)}>
                                    {showOld ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                                </IconButton>
                            </InputAdornment>
                        }
                    />
                </FormControl>

                {/* New Password */}
                <FormControl size="small" fullWidth sx={fieldSx}>
                    <InputLabel>New Password</InputLabel>
                    <OutlinedInput
                        type={showNew ? 'text' : 'password'}
                        value={newPwd}
                        label="New Password"
                        onChange={(e) => setNewPwd(e.target.value)}
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setShowNew(!showNew)}>
                                    {showNew ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                                </IconButton>
                            </InputAdornment>
                        }
                    />
                </FormControl>

                {/* Strength Bar */}
                {newPwd && (
                    <Box sx={{ mb: 2, mt: -1 }}>
                        <LinearProgress
                            variant="determinate"
                            value={strengthValue}
                            sx={{
                                height: 6,
                                borderRadius: 3,
                                bgcolor: '#e5e7eb',
                                '& .MuiLinearProgress-bar': { bgcolor: strengthColor, borderRadius: 3 }
                            }}
                        />
                        <Typography variant="caption" sx={{ color: strengthColor, fontWeight: 600, mt: 0.5, display: 'block' }}>
                            {strengthText}
                        </Typography>
                    </Box>
                )}

                {/* Confirm Password */}
                <FormControl size="small" fullWidth sx={fieldSx}>
                    <InputLabel>Confirm New Password</InputLabel>
                    <OutlinedInput
                        type={showConfirm ? 'text' : 'password'}
                        value={confirmPwd}
                        label="Confirm New Password"
                        onChange={(e) => setConfirmPwd(e.target.value)}
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setShowConfirm(!showConfirm)}>
                                    {showConfirm ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                                </IconButton>
                            </InputAdornment>
                        }
                    />
                </FormControl>

                {error && (
                    <FormHelperText error sx={{ mb: 1 }}>
                        {error}
                    </FormHelperText>
                )}
                {success && <FormHelperText sx={{ mb: 1, color: theme.palette.success.main }}>{success}</FormHelperText>}

                <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button onClick={handleClose} color="secondary" variant="outlined" size="small" sx={{ borderRadius: '8px' }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        color="secondary"
                        variant="contained"
                        size="small"
                        disabled={loading}
                        sx={{ borderRadius: '8px' }}
                    >
                        {loading ? 'Saving...' : 'Change Password'}
                    </Button>
                </Stack>
            </DialogContent>
        </Dialog>
    );
}

// ── Change Security PIN Dialog ─────────────────────────────────────────────────
function ChangePinDialog({ open, onClose, token }) {
    const dispatch = useDispatch();
    const [currentPin, setCurrentPin] = useState('');
    const [newPin, setNewPin] = useState('');
    const [confirmPin, setConfirmPin] = useState('');
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleClose = () => {
        setCurrentPin('');
        setNewPin('');
        setConfirmPin('');
        setError('');
        setLoading(false);
        onClose();
    };

    const onlyDigits = (val) => val.replace(/\D/g, '').slice(0, 4);

    const handleSubmit = async () => {
        setError('');
        if (!currentPin || !newPin || !confirmPin) {
            setError('All fields are required.');
            return;
        }
        if (!/^\d{4}$/.test(newPin)) {
            setError('New PIN must be exactly 4 digits.');
            return;
        }
        if (newPin !== confirmPin) {
            setError('New PINs do not match.');
            return;
        }
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/auth/change-pin/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ currentPin, newPin, confirmPin })
            });
            const data = await res.json();
            if (!res.ok) {
                setError(data.error || 'Failed to change PIN.');
            } else {
                handleClose();
                dispatch(
                    openSnackbar({
                        open: true,
                        message: 'Security PIN changed successfully!',
                        variant: 'alert',
                        alert: { color: 'success', variant: 'filled' },
                        close: true,
                        anchorOrigin: { vertical: 'top', horizontal: 'right' },
                        transition: 'SlideDown'
                    })
                );
            }
        } catch {
            setError('Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const fieldSx = { mb: 2, mt: 1, '& .MuiOutlinedInput-root': { borderRadius: '8px' } };

    const pinField = (label, value, setter, show, setShow) => (
        <FormControl size="small" fullWidth sx={fieldSx}>
            <InputLabel>{label}</InputLabel>
            <OutlinedInput
                type={show ? 'text' : 'password'}
                value={value}
                label={label}
                autoComplete="off"
                inputProps={{ inputMode: 'numeric', maxLength: 4 }}
                onChange={(e) => setter(onlyDigits(e.target.value))}
                endAdornment={
                    <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShow(!show)}>
                            {show ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                        </IconButton>
                    </InputAdornment>
                }
            />
        </FormControl>
    );

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontWeight: 700, pb: 0 }}>Change Security PIN</DialogTitle>
            <DialogContent sx={{ pt: 3, pb: 3, overflow: 'visible' }}>
                <input type="text" style={{ display: 'none' }} readOnly autoComplete="username" />
                <input type="password" style={{ display: 'none' }} readOnly autoComplete="new-password" />
                {pinField('Current PIN', currentPin, setCurrentPin, showCurrent, setShowCurrent)}
                {pinField('New PIN (4 digits)', newPin, setNewPin, showNew, setShowNew)}
                {pinField('Confirm New PIN', confirmPin, setConfirmPin, showConfirm, setShowConfirm)}
                {error && (
                    <FormHelperText error sx={{ mb: 1 }}>
                        {error}
                    </FormHelperText>
                )}
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button onClick={handleClose} color="secondary" variant="outlined" size="small" sx={{ borderRadius: '8px' }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        color="secondary"
                        variant="contained"
                        size="small"
                        disabled={loading}
                        sx={{ borderRadius: '8px' }}
                    >
                        {loading ? 'Saving...' : 'Change PIN'}
                    </Button>
                </Stack>
            </DialogContent>
        </Dialog>
    );
}

// ==============================|| PROFILE MENU ||============================== //

const ProfileSection = () => {
    const theme = useTheme();
    const { borderRadius } = useConfig();

    const [selectedIndex, setSelectedIndex] = useState(-1);
    const { logout, user } = useAuth();
    const [open, setOpen] = useState(false);
    const [changePwdOpen, setChangePwdOpen] = useState(false);
    const [changePinOpen, setChangePinOpen] = useState(false);
    const token = localStorage.getItem('token') || '';

    const anchorRef = useRef(null);

    const handleLogout = async () => {
        try {
            await logout();
        } catch (err) {
            console.error(err);
        }
    };

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) return;
        setOpen(false);
    };

    const handleListItemClick = (event, index) => {
        setSelectedIndex(index);
        handleClose(event);
        if (index === 0) setChangePwdOpen(true);
        if (index === 1) setChangePinOpen(true);
    };

    const handleToggle = () => setOpen((prev) => !prev);

    const prevOpen = useRef(open);
    useEffect(() => {
        if (prevOpen.current === true && open === false) anchorRef.current.focus();
        prevOpen.current = open;
    }, [open]);

    return (
        <>
            <Tooltip title="Profile">
                <Chip
                    sx={{
                        height: '48px',
                        alignItems: 'center',
                        borderRadius: '27px',
                        transition: 'all .2s ease-in-out',
                        borderColor: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light,
                        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light,
                        '&[aria-controls="menu-list-grow"], &:hover': {
                            borderColor: theme.palette.primary.main,
                            background: `${theme.palette.primary.main}!important`,
                            color: theme.palette.primary.light,
                            '& svg': { stroke: theme.palette.primary.light }
                        },
                        '& .MuiChip-label': { lineHeight: 0 }
                    }}
                    label={<IconUserCircle stroke={1.5} size="28px" color={theme.palette.primary.main} />}
                    variant="outlined"
                    ref={anchorRef}
                    aria-controls={open ? 'menu-list-grow' : undefined}
                    aria-haspopup="true"
                    onClick={handleToggle}
                    color="primary"
                />
            </Tooltip>

            <Popper
                placement="bottom"
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                modifiers={[{ name: 'offset', options: { offset: [0, 14] } }]}
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener onClickAway={handleClose}>
                        <Transitions in={open} {...TransitionProps}>
                            <Paper>
                                {open && (
                                    <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                                        <Box sx={{ p: 2, pb: 0 }}>
                                            <Stack>
                                                <Typography variant="h4">{getGreeting()},</Typography>
                                                <Typography variant="subtitle2">
                                                    {user?.firstName} {user?.lastName}
                                                </Typography>
                                            </Stack>
                                            <Divider sx={{ mt: 2 }} />
                                        </Box>
                                        <PerfectScrollbar style={{ height: '100%', maxHeight: 'calc(100vh - 250px)', overflowX: 'hidden' }}>
                                            <Box sx={{ p: 2, pt: 0 }}>
                                                <List
                                                    component="nav"
                                                    sx={{
                                                        width: '100%',
                                                        maxWidth: 350,
                                                        minWidth: 300,
                                                        backgroundColor: theme.palette.background.paper,
                                                        borderRadius: '10px',
                                                        [theme.breakpoints.down('md')]: { minWidth: '100%' },
                                                        '& .MuiListItemButton-root': { mt: 0.5 }
                                                    }}
                                                >
                                                    <ListItemButton
                                                        sx={{ borderRadius: `${borderRadius}px` }}
                                                        selected={selectedIndex === 0}
                                                        onClick={(event) => handleListItemClick(event, 0)}
                                                    >
                                                        <ListItemIcon>
                                                            <IconKey stroke={1.5} size="20px" />
                                                        </ListItemIcon>
                                                        <ListItemText primary={<Typography variant="body2">Change Password</Typography>} />
                                                    </ListItemButton>
                                                    <ListItemButton
                                                        sx={{ borderRadius: `${borderRadius}px` }}
                                                        selected={selectedIndex === 1}
                                                        onClick={(event) => handleListItemClick(event, 1)}
                                                    >
                                                        <ListItemIcon>
                                                            <IconLock stroke={1.5} size="20px" />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={<Typography variant="body2">Change Security PIN</Typography>}
                                                        />
                                                    </ListItemButton>
                                                    <Divider sx={{ my: 0.5 }} />
                                                    <ListItemButton
                                                        sx={{ borderRadius: `${borderRadius}px` }}
                                                        selected={selectedIndex === 4}
                                                        onClick={handleLogout}
                                                    >
                                                        <ListItemIcon>
                                                            <IconLogout stroke={1.5} size="20px" />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={
                                                                <Typography variant="body2">
                                                                    <FormattedMessage id="logout" />
                                                                </Typography>
                                                            }
                                                        />
                                                    </ListItemButton>
                                                </List>
                                            </Box>
                                        </PerfectScrollbar>
                                    </MainCard>
                                )}
                            </Paper>
                        </Transitions>
                    </ClickAwayListener>
                )}
            </Popper>

            <ChangePasswordDialog open={changePwdOpen} onClose={() => setChangePwdOpen(false)} token={token} />
            <ChangePinDialog open={changePinOpen} onClose={() => setChangePinOpen(false)} token={token} />
        </>
    );
};

export default ProfileSection;
